import { useTranslation } from 'react-i18next'

import { setLang } from './i18n'
import type { BusinessProfile } from './api/types'

export function Spinner() {
  return <div className="spinner" role="status" aria-label="loading" />
}

export function Banner({ kind, children }: { kind: 'error' | 'info' | 'dev'; children: React.ReactNode }) {
  return <div className={`banner banner-${kind}`}>{children}</div>
}

export function Stepper({ current, total }: { current: number; total: number }) {
  return (
    <div className="steps" aria-hidden>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className={`step-dot${i <= current ? ' active' : ''}`} />
      ))}
    </div>
  )
}

export function LangSwitch() {
  const { i18n } = useTranslation()
  const isZh = i18n.language.startsWith('zh')
  return (
    <button className="lang-switch" onClick={() => setLang(isZh ? 'en' : 'zh-Hant-TW')}>
      {isZh ? 'EN' : '中文'}
    </button>
  )
}

export function BizHeader({ profile }: { profile: BusinessProfile }) {
  const initial = profile.name?.trim().charAt(0) || '🐾'
  const hero = profile.cover_photos?.[0] || null
  return (
    <header className="biz-header">
      {hero && (
        <div className="biz-hero">
          <img src={hero} alt="" loading="eager" />
          <div className="biz-hero-veil" />
        </div>
      )}
      <div className={`biz-header-row${hero ? ' over-hero' : ''}`}>
        {profile.logo_url ? (
          <img className="biz-logo" src={profile.logo_url} alt={profile.name} />
        ) : (
          <div className="biz-logo" style={{ display: 'grid', placeItems: 'center', fontSize: 24 }}>
            {initial}
          </div>
        )}
        <div>
          <h1 className="biz-name">{profile.name}</h1>
          {profile.tagline && <p className="biz-tagline">{profile.tagline}</p>}
        </div>
      </div>
    </header>
  )
}

/** Photo gallery — the cover_photos AFTER the first (which is the hero). */
export function BizGallery({ photos }: { photos: string[] | undefined }) {
  const rest = (photos ?? []).slice(1)
  if (rest.length === 0) return null
  return (
    <div className="biz-gallery" aria-label="Photos">
      {rest.map((url, i) => (
        <a
          key={url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="biz-gallery-item"
        >
          <img src={url} alt="" loading="lazy" />
          <span className="sr-only">Photo {i + 2}</span>
        </a>
      ))}
    </div>
  )
}

/** YouTube / Vimeo intro video embed. */
export function BizVideo({
  url,
  provider,
}: {
  url: string | null | undefined
  provider: 'youtube' | 'vimeo' | null | undefined
}) {
  if (!url || !provider) return null
  let embed: string | null = null
  if (provider === 'youtube') {
    const id = url.match(/[?&]v=([A-Za-z0-9_-]+)/)?.[1] || url.match(/youtu\.be\/([A-Za-z0-9_-]+)/)?.[1]
    if (id) embed = `https://www.youtube-nocookie.com/embed/${id}`
  } else if (provider === 'vimeo') {
    const id = url.match(/vimeo\.com\/(\d+)/)?.[1]
    if (id) embed = `https://player.vimeo.com/video/${id}`
  }
  if (!embed) return null
  return (
    <div className="biz-video">
      <iframe
        src={embed}
        title="Intro video"
        loading="lazy"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  )
}
